import React, {useCallback, useState} from 'react';
import {
    errorSet,
    errorsSet,
    processingPaymentSet,
    selectConfirmationToken,
    selectConnectedAccountId,
    selectIsCardVerified,
    selectIsLoadingPaymentInfo,
    selectPaymentIntentId,
    selectPaymentMethod,
    selectPaymentType,
    selectProcessingPayment,
    selectUpdatingSession,
} from 'components/customer/Confirmation/store/confirmationSlice';
import {
    useAccountPaymentMutation,
    useInvoicePaymentMutation,
    useLazyConfirmPaymentQuery,
} from 'components/customer/Confirmation/store/gatewayApi';
import {useJobContext} from 'contexts/JobContext';
import {useAppContext} from 'contexts/ApplicationContext';
import {PartialJob} from 'shared/types/PartialJob';
import {useAppDispatch, useAppSelector} from 'store/customer';
import {staticMessage} from 'components/customer/Confirmation/Gateways/Stripe/PaymentForm';
import {StripeAction} from 'components/customer/Confirmation/Gateways/Stripe/entity/StripeConfirmation';
import {PayButton as ButtonComponent} from 'components/customer/Confirmation/Gateways/Stripe/PayButton';
import {usePaymentContext} from 'components/customer/Confirmation/Payment';
import {PaymentType} from 'components/manufacturer/Payments/entity/Deposit';
import {useSubmitJob} from 'components/customer/Job/helpers/useSubmitJob';
import {useNavigate} from 'react-router-dom';

interface ErrorResponse {
    status?: number;
    data?: {
        success?: number;
        error?: string | {messages: string[]};
    };
}

export const PayButton = () => {
    const {extraActionHandler, isBalancePayment} = usePaymentContext();
    const {job} = useJobContext() as PartialJob;
    const {userProfile} = useAppContext();

    const dispatch = useAppDispatch();

    const paymentType = useAppSelector(selectPaymentType);
    const connectedAccountId = useAppSelector(selectConnectedAccountId);
    const paymentIntentId = useAppSelector(selectPaymentIntentId);
    const isLoadingPaymentInfo = useAppSelector(selectIsLoadingPaymentInfo);
    const confirmationToken = useAppSelector(selectConfirmationToken);
    const isCardVerified = useAppSelector(selectIsCardVerified);
    const updatingSession = useAppSelector(selectUpdatingSession);
    const paymentMethod = useAppSelector(selectPaymentMethod);
    const processingPayment = useAppSelector(selectProcessingPayment);

    const [processingLocalPayment, setProcessingLocalPayment] = useState(false);

    const navigate = useNavigate();

    const setProcessingPayment = useCallback((state: boolean) => {
        dispatch(processingPaymentSet(state));
    }, []);

    const [confirmPayment, {isLoading: isConfirmingPayment}] =
        useLazyConfirmPaymentQuery();
    const [sendInvoice, {isLoading: isInvoicing}] = useInvoicePaymentMutation();
    const [payWithAccount, {isLoading: isAccountPaying}] =
        useAccountPaymentMutation();

    const submitJob = useSubmitJob(true);

    const processPayment = useCallback(async () => {
        try {
            setProcessingPayment(true);
            dispatch(errorsSet([]));

            const {data, error} = await confirmPayment({
                manufacturerId: userProfile.manufacturerId,
                paymentIntentId,
                confirmationTokenId: confirmationToken,
                jobId: job.id,
            });

            if (error) {
                const typedError = error as ErrorResponse;
                if (typeof typedError?.data?.error == 'string') {
                    throw new Error(typedError.data.error);
                } else if (Array.isArray(typedError?.data?.error?.messages)) {
                    throw new Error(
                        typedError?.data?.error?.messages.join('<br />')
                    );
                } else {
                    throw new Error(staticMessage);
                }
            }

            if (data.requiresAction) {
                if (data.actionType == StripeAction.REDIRECT) {
                    window.location.href = data.redirectUrl;
                    return;
                } else if (data.actionType == StripeAction.STRIPE_SDK) {
                    const {error} = await extraActionHandler.action(
                        data.clientSecret
                    );

                    if (error) {
                        dispatch(errorSet(error.message));
                        return;
                    }
                } else if (data.actionType == StripeAction.QR_CODE) {
                    // NOTE: handle this later if AU and NZ banks implement this
                    // yet not available only for services like wechat pay,
                    // PayNow and PromptPay which are used in SE Asian countries
                } else if (data.actionType == StripeAction.MICRO_DEPOSITS) {
                    // NOTE: handle this later if AU and NZ banks implement this
                    // yet not available, only for europe
                }
            }

            if (data.redirectUrl) {
                // this is basically done and redirect to confirmation page.
                let redirectUrl = data.redirectUrl;

                if (isBalancePayment) {
                    redirectUrl += '&balance-payment=1';
                }

                window.location.href = redirectUrl;
            }
        } catch (e) {
            setProcessingPayment(false);
            if (e instanceof Error) {
                if (typeof e.message === 'string') {
                    if (
                        e.message.indexOf(
                            'The confirmation token has already been used to confirm a previous PaymentIntent'
                        ) > -1
                    ) {
                        dispatch(
                            errorSet(
                                'Your payment didn’t go through. Please check your card details or try another card.'
                            )
                        );
                    } else {
                        dispatch(errorSet(e.message));
                    }
                }
            } else {
                dispatch(errorSet(staticMessage));
            }
        } finally {
            setProcessingPayment(false);
        }
    }, [
        confirmPayment,
        confirmationToken,
        paymentIntentId,
        job,
        staticMessage,
        connectedAccountId,
        paymentIntentId,
        userProfile,
        isBalancePayment,
    ]);

    const processInvoice = useCallback(async () => {
        setProcessingLocalPayment(true);
        try {
            if (!isBalancePayment) {
                // TODO: this API might need to be able to process deposit/full invoice
                // use paymentMethod.type to determine this and send back to BE
                await sendInvoice({
                    jobId: job.id,
                    paymentMethod,
                });

                if (submitJob && typeof submitJob.action == 'function') {
                    await submitJob.action(undefined);
                }
            }

            void navigate(
                `/v2/job/${job.displayId}/invoice/?narrow-header=1&payment-method=${paymentMethod}`
            );
        } catch (e) {
            dispatch(errorSet(staticMessage));
            setProcessingLocalPayment(false);
            // no need to reset to false if success as page will redirect
        }
    }, [
        sendInvoice,
        job,
        submitJob,
        navigate,
        isBalancePayment,
        paymentMethod,
    ]);

    const processAccount = useCallback(async () => {
        setProcessingLocalPayment(true);
        try {
            await payWithAccount({jobId: job.id});

            if (submitJob && typeof submitJob.action == 'function') {
                await submitJob.action(undefined);
            }

            // may be this needs to go to a confirmation page
            void navigate(`/v2/`);
        } catch (e) {
            dispatch(errorSet(staticMessage));
            setProcessingLocalPayment(false);
            // no need to reset to false if success as page will redirect
        }
    }, [submitJob, navigate, payWithAccount, job]);

    const clickHandler = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            if (paymentType == PaymentType.GATEWAY) {
                void processPayment();
            } else if (paymentType == PaymentType.INVOICE) {
                void processInvoice();
            } else {
                void processAccount();
            }
        },
        [paymentType, processPayment, processInvoice, processAccount]
    );

    return (
        <ButtonComponent
            iconName={
                paymentType == PaymentType.GATEWAY
                    ? 'PaymentCard.svg'
                    : 'Button-Tick.svg'
            }
            text={paymentType == PaymentType.GATEWAY ? 'Pay' : 'Confirm'}
            hide={false}
            disabled={
                paymentType == PaymentType.GATEWAY
                    ? updatingSession ||
                      processingPayment ||
                      isConfirmingPayment ||
                      isLoadingPaymentInfo ||
                      !isCardVerified
                    : isInvoicing || processingLocalPayment || isAccountPaying
            }
            isLoading={
                updatingSession ||
                processingPayment ||
                processingLocalPayment ||
                isConfirmingPayment ||
                isLoadingPaymentInfo ||
                isInvoicing ||
                isAccountPaying
            }
            onClick={clickHandler}
        />
    );
};
